// src/components/loginforms/SignUpForm.js
import React, { useState, useEffect } from "react";
import styles from "../Header.module.css";
import PasswordValidation from "./PasswordValidation";
import UsernameValidation from "./UsernameValidation";
import { useAuth } from "../../context/AuthProvider";

const SignUpForm = ({ setActiveTab }) => {
  const [formState, setFormState] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [localError, setLocalError] = useState("");

  // Use the auth context
  const { signUp, signUpWithGoogle, error: authError, loading } = useAuth();

  // Update local error when auth error changes
  useEffect(() => {
    if (authError) {
      setLocalError(authError);
    }
  }, [authError]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    setLocalError(""); // Clear errors when user types
  };

  // Handle standard email signup
  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setLocalError("");

    // Basic validation
    if (!formState.username || !formState.email || !formState.password) {
      setLocalError("Please complete all fields");
      return;
    }

    if (formState.password !== formState.confirmPassword) {
      setLocalError("Passwords do not match");
      return;
    }

    try {
      // Call the signUp method from auth context - lowercase conversion handled in AuthProvider
      await signUp(formState.username, formState.email, formState.password);
      // The verification screen will be shown in HeaderTabs based on verificationPending state
    } catch (error) {
      // This is handled by the auth context and will set authError
      console.error("Sign-up error:", error);
    }
  };

  // Handle Google signup
  const handleGoogleSignUp = async () => {
    setLocalError("");

    if (!formState.username.trim()) {
      setLocalError("Please enter a username first");
      return;
    }

    // Clear any existing auth state to prevent conflicts
    if (typeof window !== "undefined" && window.sessionStorage) {
      // Set domain for auth to ensure cookies work properly
      sessionStorage.setItem("firebase:AuthEventDomain", window.location.hostname);

      // Clear any stale auth states
      Object.keys(sessionStorage).forEach((key) => {
        if (
          key.startsWith("firebaseui:") ||
          (key.startsWith("firebase:auth:") && !key.includes(window.location.hostname))
        ) {
          console.log("Removing stale session item:", key);
          sessionStorage.removeItem(key);
        }
      });
    }

    try {
      // Call the signUpWithGoogle method from auth context - lowercase conversion handled in AuthProvider
      await signUpWithGoogle(formState.username);
    } catch (error) {
      console.error("Google signup error:", error);

      // Provide helpful error messages
      if (
        error.code === "auth/popup-closed-by-user" ||
        error.code === "auth/redirect-cancelled-by-user"
      ) {
        setLocalError("Sign-up was cancelled. Please try again.");
      } else if (error.code === "auth/popup-blocked") {
        setLocalError(
          "Popup was blocked. Please allow popups or try again on a different device."
        );
      } else if (error.code === "auth/network-request-failed") {
        setLocalError(
          "Network error. Please check your connection and try again."
        );
      } else if (error.code === "auth/account-exists-with-different-credential") {
        setLocalError("An account already exists with the same email. Please sign in with the correct provider.");
      } else {
        setLocalError("Failed to sign up with Google: " + (error.message || "Unknown error"));
      }

      // Offer to fix auth state if there's an issue
      if (
        error.code?.includes("auth/") &&
        document.querySelector(`.${styles.errorText}`)
      ) {
        const clearAuthState = () => {
          Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith("firebase:") || key.startsWith("firebaseui:")) {
              sessionStorage.removeItem(key);
            }
          });
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith("pendingGoogle") || key.startsWith("googleSignIn")) {
              localStorage.removeItem(key);
            }
          });
          window.location.reload();
        };

        const fixButton = document.createElement("button");
        fixButton.innerText = "Fix Sign-up Issues";
        fixButton.onclick = clearAuthState;
        fixButton.className = styles.fixAuthButton;

        // Add to the error message element
        setTimeout(() => {
          const errorElem = document.querySelector(`.${styles.errorText}`);
          if (errorElem && !errorElem.querySelector(".fix-auth-button")) {
            errorElem.appendChild(fixButton);
          }
        }, 100);
      }
    }
  };

  return (
    <div className={styles.signUpForm}>
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Please wait...</p>
        </div>
      ) : (
        <form onSubmit={handleEmailSignUp}>
          <div className={styles.authFormHeader}>
            <h2>Create your Linkist</h2>
            <p>Choose a username and start building your public profile.</p>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignUp}
            className={`${styles.customGoogleButton} ${styles.animate}`}
            disabled={loading}
          >
            <img
              src="/assets/google-icon.svg"
              alt="Google Icon"
              className={styles.googleIconSignUp}
            />
            <span>Sign up with Google</span>
          </button>

          {localError && <div className={styles.errorText}>{localError}</div>}

          <div className={styles.divider}>or</div>

          <input
            type="text"
            name="username"
            placeholder="Choose your username"
            value={formState.username}
            onChange={handleInputChange}
            disabled={loading}
          />

          <UsernameValidation
            username={formState.username}
            setUsername={(username) =>
              setFormState((prev) => ({ ...prev, username }))
            }
          />

          <input
            type="email"
            name="email"
            placeholder="Email address"
            value={formState.email}
            onChange={handleInputChange}
            disabled={loading}
          />

          <input
            type="password"
            name="password"
            placeholder="Password"
            value={formState.password}
            onChange={handleInputChange}
            disabled={loading}
          />

          <input
            type="password"
            name="confirmPassword"
            placeholder="Confirm password"
            value={formState.confirmPassword}
            onChange={handleInputChange}
            disabled={loading}
          />

          <PasswordValidation
            password={formState.password}
            setPassword={(pass) =>
              setFormState((prev) => ({ ...prev, password: pass }))
            }
          />

          <button type="submit" disabled={loading}>
            Create account
          </button>

          
        </form>
      )}
    </div>
  );
};

export default SignUpForm;
